/** Must match the Cognito user pool group created in infra (`AdminGroup`). */
export const COGNITO_ADMIN_GROUP = "Admin" as const;

/**
 * Reserved role name used by the automation M2M principal after `getAuthContext` maps
 * a client_credentials token with scope `recobra/automation`. Not granted via
 * Cognito group membership on human sessions.
 */
export const COGNITO_AUTOMATION_GROUP = "Automation" as const;

/** Cognito groups assignable via admin APIs (English canonical names). */
export const COGNITO_PROFILE_GROUPS = [
  "Manager",
  "Negotiator",
  "Legal",
  "Operator",
  "Customer",
  "Partner",
  "Expert",
] as const;

/** All application profile groups including Admin. */
export const COGNITO_ALL_PROFILE_GROUPS = [COGNITO_ADMIN_GROUP, ...COGNITO_PROFILE_GROUPS] as const;

/**
 * Internal case-handling staff — excludes the external-facing groups
 * (`Customer`, `Partner`, `Expert`). Used to gate endpoints that have no
 * external-facing equivalent yet (dashboard, global tasks, manual case edits).
 */
export const COGNITO_INTERNAL_STAFF_GROUPS = [
  COGNITO_ADMIN_GROUP,
  "Manager",
  "Negotiator",
  "Legal",
  "Operator",
] as const;

export const COGNITO_CUSTOMER_CASE_SUMMARY_GROUPS = [
  ...COGNITO_INTERNAL_STAFF_GROUPS,
  "Customer",
] as const;

/** Groups that may only be assigned in the AWS Cognito console. */
const COGNITO_CONSOLE_ONLY_GROUPS = [COGNITO_ADMIN_GROUP] as const;

/** Service identities must never appear on a person's account or assignment UI. */
const COGNITO_SERVICE_ONLY_GROUPS = [COGNITO_AUTOMATION_GROUP] as const;

export const isConsoleOnlyCognitoGroup = (groupName: string): boolean =>
  (COGNITO_CONSOLE_ONLY_GROUPS as readonly string[]).includes(groupName);

export const isServiceOnlyCognitoGroup = (groupName: string): boolean =>
  (COGNITO_SERVICE_ONLY_GROUPS as readonly string[]).includes(groupName);

export const filterPoolAssignableCognitoGroups = <T extends string>(groups: readonly T[]): T[] =>
  groups.filter((group) => !isConsoleOnlyCognitoGroup(group) && !isServiceOnlyCognitoGroup(group));

export const filterVisibleUserCognitoGroups = <T extends string>(groups: readonly T[]): T[] =>
  groups.filter((group) => !isServiceOnlyCognitoGroup(group));
